
import { Component, Vue } from 'vue-property-decorator';
import {
  ChatInterface,
  ChatRepositoryInterface,
  FilterRequestData,
  ProfileMapInterface,
} from '@/services/QC-V2/chat/interfaces';
import Themeable from 'vuetify/lib/mixins/themeable';
import ChatsFilters from '@/components/qa/chat/ChatsFilters.vue';
import ChatsRows from '@/components/qa/chat/ChatsRows.vue';
import ChatMessages from '@/components/qa/chat/ChatMessages.vue';
import ChatDetails from '@/components/qa/chat/ChatDetails.vue';
import ChatPrivateNotes from '@/components/qa/chat/ChatPrivateNotes.vue';
import { ProfileRepositoryInterface } from '@/services/Users-profile/interfaces';
import ChatReviewsDetails from '@/components/qa/chat/ChatReviewsDetails.vue';
import { ScoreTypeInterface, ScoreTypeRepositoryInterface } from '@/services/QC-V2/score-type/interfaces';
import { ScoreGradeInterface, ScoreGradeRepositoryInterface } from '@/services/QC-V2/score-grade/interfaces';
import { TagInterface, TagRepositoryInterface } from '@/services/QC-V2/tag/interfaces';
import ArchiveChatComments from '@/components/qa/chat/ArchiveChatComments.vue';

@Component({
  mixins: [
    Themeable,
  ],
  components: {
    ArchiveChatComments,
    ChatReviewsDetails,
    ChatsFilters,
    ChatsRows,
    ChatMessages,
    ChatDetails,
    ChatPrivateNotes,
  },
  inject: [
    'chatRepositoryV2',
    'profileRepository',
    'scoreTypeRepositoryV2',
    'scoreGradeRepositoryV2',
    'tagRepositoryV2',
  ],
})
export default class Archive extends Vue {
  private readonly chatRepositoryV2!: ChatRepositoryInterface;
  private readonly profileRepository!: ProfileRepositoryInterface;
  private readonly scoreTypeRepositoryV2!: ScoreTypeRepositoryInterface;
  private readonly scoreGradeRepositoryV2!: ScoreGradeRepositoryInterface;
  private readonly tagRepositoryV2!: TagRepositoryInterface;

  private loading: boolean = true;

  private chats: ChatInterface[] = [];
  private chat: ChatInterface = {} as ChatInterface;

  private profilesMap: ProfileMapInterface = {};

  private scoreTypes: ScoreTypeInterface[] = [];
  private scoreGrades: ScoreGradeInterface[] = [];
  private tags: TagInterface[] = [];

  private filter: FilterRequestData | undefined = {};

  private itemsPerPage: number = 15;
  private page: number = 1;
  private hasNextPage: boolean = true;


  private get windowHeight(): number {
    return window.screen.height - 300;
  }

  private async created(): Promise<void> {
    try {
      await this.fetchScores();
      await this.fetchTags();
      await this.initialize();
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async initialize(filter?: FilterRequestData): Promise<void> {
    try {
      this.page = 1;
      this.hasNextPage = true;
      this.filter = filter;
      this.chats = await this.chatRepositoryV2.getChatArchive({
        ...filter,
        page: this.page,
      });

      if (this.chats.length < this.itemsPerPage) {
        this.hasNextPage = false;
      }

      if (this.$route.params.chatId) {
        await this.getChat(this.$route.params.chatId);
      }

      if (window.screen.height >= 1300) {
        await this.loadMoreItems();
      }

      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchTags(): Promise<void> {
    this.tags = await this.tagRepositoryV2.getActiveTags();
  }

  private async fetchScores(): Promise<void> {
    this.scoreTypes = await this.scoreTypeRepositoryV2.getAll();
    this.scoreGrades = await this.scoreGradeRepositoryV2.getAll();
  }

  private async getChat(id: string): Promise<void> {
    if (this.chat.id === id) {
      return;
    }

    try {
      this.loading = true;
      const chat = await this.chatRepositoryV2.getChatById(id);
      if (!this.$route.params.chatId || this.$route.params.chatId !== id) {
        this.$route.params.chatId = chat.id;
        await this.$router.replace({ name: 'chat_archive', params: { chatId: chat.id } });
      }
      await this.fetchChatProfilesData(chat);
      this.chat = chat;
      this.loading = false;
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }

  private async fetchChatProfilesData(chat: ChatInterface): Promise<void> {
    let ids: string[] = [];

    chat.reviews.forEach((review) => {
      if (!ids.includes(review.reviewerId)) {
        ids.push(review.reviewerId);
      }

      if (!ids.includes(review.reviewedId)) {
        ids.push(review.reviewedId);
      }

      if (!review.comments.length) {
        return;
      }

      review.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    chat.comments.forEach((comment) => {
      if (!ids.includes(comment.author)) {
        ids.push(comment.author);
      }
    });

    chat.notes.forEach((note) => {
      if (!ids.includes(note.author)) {
        ids.push(note.author);
      }
    });

    chat.messages.forEach((message) => {
      if (!message.comments.length) {
        return;
      }

      message.comments.forEach((comment) => {
        if (!ids.includes(comment.author)) {
          ids.push(comment.author);
        }
      });
    });

    ids = ids.filter((id, index, self) => self.indexOf(id) === index);
    const profiles = await this.profileRepository.getBaseProfileDataByUsersIds(ids);
    profiles.forEach((profile) => {
      this.profilesMap[profile.userId] = profile;
    });
  }

  private async handleScroll(
    event: { target: { scrollTop: number, clientHeight: number, scrollHeight: number } },
  ): Promise<void> {
    if ((event.target.scrollTop + event.target.clientHeight + 10 < event.target.scrollHeight) || !this.hasNextPage) {
      return;
    }

    await this.loadMoreItems();
  }

  private async loadMoreItems(): Promise<void> {
    if (!this.hasNextPage) {
      return;
    }

    this.page += 1;
    try {
      const chats = await this.chatRepositoryV2.getChatArchive({
        ...this.filter,
        page: this.page,
      });

      if (chats.length < this.itemsPerPage) {
        this.hasNextPage = false;
      }

      this.chats.push(...chats);
    } catch (e) {
      this.$errorDialog.show(e.response?.data?.message ? e.response.data.message : e.message);
    }
  }
}
